import { FormEvent, useEffect, useState } from 'react';
import { UnitsApi } from '../../api/resources';
import { ApiError } from '../../api/client';
import type { Unit } from '../../api/types';
import { useToast } from '../../context/ToastContext';

const emptyForm = { code: '', name: '' };

export function Units() {
  const { notify } = useToast();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const result = await UnitsApi.list(true);
      setUnits(result.units);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar las unidades.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await UnitsApi.create(form);
      notify('Unidad creada.', 'success');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible crear la unidad.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u: Unit) {
    try {
      if (u.isActive) await UnitsApi.deactivate(u.id);
      else await UnitsApi.activate(u.id);
      notify('Estado actualizado.', 'success');
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible actualizar el estado.', 'error');
    }
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Unidades de medida</h1>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cerrar' : '+ Nueva unidad'}
        </button>
      </div>

      {showForm && (
        <div className="card">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="ucode">Codigo</label>
                <input id="ucode" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="uname">Nombre</label>
                <input id="uname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Crear unidad'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-block">Cargando...</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Nombre</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id}>
                    <td>{u.code}</td>
                    <td>{u.name}</td>
                    <td>
                      <span className={`pill ${u.isActive ? 'pill-active' : 'pill-inactive'}`}>{u.isActive ? 'Activa' : 'Inactiva'}</span>
                    </td>
                    <td>
                      <button type="button" className={`btn btn-sm ${u.isActive ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(u)}>
                        {u.isActive ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
